import React from 'react';
import { Warehouse, Key, DollarSign, Building2, TrendingUp, AlertCircle } from 'lucide-react';

const KPICard = ({ title, value, sub, icon: Icon, color, trend, format = 'number' }) => {
  const fmt = (v) => {
    if (format === 'currency') return `$${Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
    if (format === 'pct') return `${v}%`;
    if (format === 'number') return Number(v).toLocaleString();
    if (format === 'sqft') return `${Number(v).toLocaleString()} sqft`;
    return v;
  };

  return (
    <div className="relative bg-gray-800/60 border border-gray-700/60 rounded-xl p-4 overflow-hidden group hover:border-gray-600 transition-all duration-300">
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{ background: `radial-gradient(circle at 80% 20%, ${color}15 0%, transparent 60%)` }} />
      <div className="relative">
        <div className="flex items-start justify-between mb-1">
          <p className="text-gray-400 text-xs font-medium uppercase tracking-wider">{title}</p>
          <div className="p-1.5 rounded-lg" style={{ background: `${color}20` }}>
            <Icon size={15} style={{ color }} />
          </div>
        </div>
        <p className="text-2xl font-bold tabular-nums mt-1" style={{ color }}>
          {fmt(value)}
        </p>
        {sub && (
          <div className="flex items-center gap-1 mt-1.5">
            {trend === 'up' && <TrendingUp size={11} className="text-emerald-400" />}
            {trend === 'down' && <TrendingUp size={11} className="text-red-400" />}
            <span className={`text-xs ${
              trend === 'up' ? 'text-emerald-400' : 
              trend === 'down' ? 'text-red-400' : 'text-gray-400'
            }`}>{sub}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const HangarLeaseKPIs = ({ data }) => {
  const {
    totalHangars = 0,
    occupiedHangars = 0,
    occupancyRate = 0,
    activeLeases = 0,
    monthlyRevenue = 0,
    totalDeposits = 0,
    totalArrears = 0,
    expiringLeasesCount = 0
  } = data;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <KPICard 
        title="Occupancy Rate" 
        value={occupancyRate} 
        icon={TrendingUp}
        color="#10b981" 
        format="pct"
        sub={`${occupiedHangars} / ${totalHangars} hangars`}
      />
      <KPICard 
        title="Active Leases" 
        value={activeLeases} 
        icon={Key}
        color="#38bdf8" 
        format="number"
        sub={`${expiringLeasesCount} expiring soon`}
        trend={expiringLeasesCount > 0 ? 'down' : 'up'}
      />
      <KPICard 
        title="Monthly Revenue" 
        value={monthlyRevenue} 
        icon={DollarSign}
        color="#f59e0b" 
        format="currency"
      />
      <KPICard 
        title="Security Deposits" 
        value={totalDeposits} 
        icon={Building2}
        color="#a78bfa" 
        format="currency"
      />
      {totalArrears > 0 && (
        <KPICard 
          title="Rent Arrears" 
          value={totalArrears} 
          icon={AlertCircle}
          color="#ef4444" 
          format="currency"
          trend="down"
          sub="Overdue payments"
        />
      )}
    </div>
  );
};

export default HangarLeaseKPIs;